import React, { MouseEvent, useEffect } from 'react';
import './style.scss';
import { store, useAppSelector } from 'careand-redux/store';
import AppointmentUtils from 'services/AppointmentUtils';
import { PatientImage, PhysicianImage } from 'components/Common/UserImage';
import PatientUtils from 'services/PatientUtils';
import StaffUtils from 'services/StaffUtils';
import CalendarUtils from 'services/CalendarUtils';
import UnreadBadge from 'components/Common/UnreadBadge';
import Icon from 'components/Utils/NewIcon';
import { getUnwrappedDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { Appointment, Patient, UserRoleEnum } from 'generated/api';

/**
 * Batch get patients
 * Tries to group multiple patient requests into one getMultiplePatientsCall
 * It doesn't fetch patients that are already in the store
 * Should be used only when performance > having the last version of the Patient
 */
class PatientFetchCoordinator {
  fetched!: Set<string>;
  queue = new Set<string>();
  queueTimer: any = undefined;
  private QUEUE_TIMEOUT_MS = 500;

  constructor() {
    this.updateFetchedSet();
  }

  updateFetchedSet() {
    this.fetched = new Set(Object.keys(store.getState().patients.byId));
  }

  fetchPatient(id: string) {
    if (this.fetched.has(id) || this.queue.has(id)) {
      return;
    }
    if (this.queueTimer) {
      clearTimeout(this.queueTimer);
    }
    this.queue.add(id);
    this.queueTimer = setTimeout(() => {
      this.fetchPatients(Array.from(this.queue));
      this.queue = new Set();
    }, this.QUEUE_TIMEOUT_MS);
  }

  async fetchPatients(ids: string[]): Promise<Patient[] | undefined> {
    this.updateFetchedSet();
    const dispatch = getUnwrappedDispatch(store.dispatch);
    const fetchList = ids.filter((it) => it && !this.fetched.has(it));
    if (fetchList.length === 0) {
      return;
    }
    fetchList.forEach((id) => this.fetched.add(id));
    return dispatch(
      api.Patients.getMultiplePatients({ multipleValuesPayload: { values: fetchList } }),
    );
  }
}

const coordinator = new PatientFetchCoordinator();

const NewAppointmentBadge = ({
  appointment,
  onClick,
  onShowRating,
  showPatient,
  patientFetchCoordinator,
  autoFetchPatient,
}: {
  appointment: Appointment;
  onClick?: () => void;
  onShowRating?: (appt: Appointment) => void;
  showPatient?: boolean;
  patientFetchCoordinator?: PatientFetchCoordinator;
  autoFetchPatient?: boolean;
}) => {
  const clinics = useAppSelector((state) => state.resource.clinics);
  const physician = useAppSelector((state) => state.physicians.byId[appointment.physician_id!]);
  const patient = useAppSelector((state) => state.patients.byId[appointment.patient_id!]);
  const role = useAppSelector((state) => state.authentication.role);

  useEffect(() => {
    if (autoFetchPatient && appointment.patient_id) {
      coordinator.fetchPatient(appointment.patient_id);
    }
  }, [autoFetchPatient, appointment.patient_id]);

  let statusClassName = `status status-${appointment.status}`;

  let icon = AppointmentUtils.getPatientIcon(appointment);

  const clinicName = clinics.find(({ id }) => id === appointment.clinic_id)?.name;

  let description =
    appointment.type === AppointmentUtils.getAppointmentTypes().clinic ||
    appointment.type === AppointmentUtils.getAppointmentTypes().lab
      ? clinicName
      : AppointmentUtils.getAppointmentDescription(appointment);

  const showRatingButton =
    onShowRating &&
    appointment.status === AppointmentUtils.getAppointmentStatus().completed &&
    !appointment.rating;

  function onClickRating(event: MouseEvent<HTMLSpanElement>) {
    event.stopPropagation();
    event.preventDefault();
    onShowRating?.(appointment);
  }

  return (
    <>
      <div className="new-appointment-badge-wrapper">
        <div
          className="new-appointment-badge"
          onClick={onClick}
          id={`appointment-badge-${appointment.id}`}
        >
          <div className="badge-top-row">
            {showPatient ? (
              <PatientImage user={patient} width={71.5} />
            ) : (
              <PhysicianImage user={physician} width={71.5} />
            )}
            <div className="text-content">
              <div className="name">
                {showPatient ? PatientUtils.getName(patient) : StaffUtils.getName(physician)}
              </div>
              <div className="date">
                {CalendarUtils.format(appointment.date!, 'MMM DD, YYYY [at] h:mm a')}
              </div>
              <div className={statusClassName}>{appointment.status}</div>
              {!appointment.read && role === UserRoleEnum.Patient ? (
                <UnreadBadge unreadText="  " />
              ) : null}
            </div>
          </div>
          <div className="bottom-row">
            <Icon src={icon} width="20px" height="20px" />
            <span>{description}</span>
            {!showRatingButton && <span className="button">View Details</span>}
            {showRatingButton && (
              <span className="button" onClick={onClickRating}>
                Rate Appointment
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NewAppointmentBadge;
